import React, { Dispatch, FormEvent, SetStateAction, useEffect, useState, useMemo } from 'react';
import styles from './addModal.module.scss';
import Modal from '../../../../components/modal/Modal';
import { TextInput, TextInputValue } from '../../../../components/text-input/TextInput';
import Button from '../../../../components/button/Button';
import { isEmptyObject, validateIsEmpty } from '../../../../utils/validation';
import Checkbox from '../../../../components/checkbox/Checkbox';
import { gql, useMutation } from '@apollo/client';
import { addDays, formatDate } from '../../../../utils/time';
import { HeToken } from '@api/graphql-codegen';
import LocalConfirmation from '@src/components/local-confirmation/LocalConfirmation';
import { logger } from '../../../../utils/logger';

type Props = {
  open: boolean;
  onClose: () => void;
  token?: HeToken;
  onCompleted: () => void;
};

const emptyErrors = {
  notes: ''
};

type CreateTokenInput = {
  token?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  association?: string;
  notes?: string;
  expireAt: string;
  blockAll?: boolean;
  expired?: boolean;
}

type CreateTokenResponse = {
  heCreateToken: boolean;
}

type UpdateTokenResponse = {
  heUpdateToken: boolean;
}

type deleteTokenResponse = {
  heDeleteToken: boolean;
}

const createMutation = gql`
  mutation heCreateToken($input: HECreateTokenInput!) {
    heCreateToken(input: $input)
  }
`;

const updateMutation = gql`
  mutation heUpdateToken($input: HEUpdateTokenInput!) {
    heUpdateToken(input: $input)
  }
`;

const deleteMutation = gql`
  mutation heDeleteToken($input: HEDeleteTokenInput!) {
    heDeleteToken(input: $input)
  }
`;

const today = formatDate(addDays(new Date(), 1).valueOf(), 'yyyy-MM-dd');

function AddModal(props: Props) {
  const { open, onClose, token, onCompleted } = props;

  const [createToken, { loading: createLoading }] = useMutation<CreateTokenResponse, { input: CreateTokenInput }>(createMutation, {
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(response) {
      if (response.heCreateToken) {
        onCompleted();
        onClose();
        clearFields();
      }
    }
  });

  const [updateToken, { loading: editLoading }] = useMutation<UpdateTokenResponse, { input: CreateTokenInput }>(updateMutation, {
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(response) {
      if (response.heUpdateToken) {
        onCompleted();
        onClose();
        clearFields();
      }
    }
  });

  const [deleteToken, { loading: deleteLoading }] = useMutation<deleteTokenResponse>(deleteMutation, {
    onError(err) {
      setBeError(err.message);
    },
    onCompleted(response) {
      if (response.heDeleteToken) {
        onCompleted();
        onClose();
        clearFields();
      }
    },
  });

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [association, setAssociation] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [expireAt, setExpireAt] = useState<string>('');
  const [blockAll, setBlockAll] = useState(false);
  const [expired, setExpired] = useState(false);

  const [error, setError] = useState(emptyErrors);
  const [beError, setBeError] = useState('');
  const isEdit = !!token;

  const [removeConfirmation, setRemoveConfirmation] = useState(false);

  useEffect(() => {
    setFirstName(token?.firstName ?? '');
    setLastName(token?.lastName ?? '');
    setEmail(token?.email ?? '');
    setAssociation(token?.association ?? '');
    setNotes(token?.notes ?? '');
    setExpireAt(token?.expireAt ? token.expireAt.split(' ')[0] : '');
    setBlockAll(false);
    setExpired(token?.expired ?? false);
  }, [token]);

  useEffect(() => {
    if (open && !expireAt && token?.expireAt) {
      const expireDate = parseInt(token?.expireAt ? token?.expireAt.split(' ')[0] : '');
      if (expireDate) {
        setExpireAt(formatDate(expireDate, 'yyyy-MM-dd'));
      }
    }
  }, [token, open, expireAt])

  const handleChangeText = (setter: Dispatch<SetStateAction<string>>, key?: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setter(value as string);
    if (key) {
      setError({
        ...error,
        [key]: '',
      });
    }
  };

  const clearFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setAssociation('');
    setNotes('');
    setExpireAt('');
    setBlockAll(false);
    setExpired(false);
    setBeError('');
    setError(emptyErrors);
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const validate = () => {
    const errors: any = {};
    if (validateIsEmpty(firstName) && validateIsEmpty(lastName) && validateIsEmpty(email) && validateIsEmpty(notes)) {
      errors.notes = 'Please leave a note for the link';
    }
    return errors;
  }

  const handleEdit = (e: FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (isEmptyObject(errors)) {
      setBeError('');
      updateToken({
        variables: {
          input: {
            token: token!.token,
            firstName,
            lastName,
            email,
            association,
            notes,
            expireAt: expireAt ? new Date(expireAt).valueOf().toString() : '',
            blockAll,
            expired
          }
        }
      });
    } else {
      logger.debug(`[e]`, errors);
      setError(errors);
    }
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (isEmptyObject(errors)) {
      setBeError('');
      createToken({
        variables: {
          input: {
            firstName,
            lastName,
            email,
            association,
            notes,
            expireAt: expireAt ? new Date(expireAt).valueOf().toString() : ''
          }
        }
      });
    } else {
      logger.debug(`[e]`, errors);
      setError(errors);
    }
  };

  const handleChangeBlockAll = (value: boolean) => setBlockAll(value);
  const handleChangeExpired = (value: boolean) => setExpired(value);

  const isActive = useMemo(() => {
    if (!token) return false;
    if (token.expired) return false;
    const now = new Date().valueOf();
    if (!token.expireAt || Number(token.expireAt) > now) {
      return true;
    }
    return false;
  }, [token]);

  const handleRemove = () => {
    token && deleteToken({
      variables: {
        input: { token: token.token }
      }
    });
  };

  const handleConfirmRemove = () => {
    handleRemove();
    setRemoveConfirmation(false);
  };

  const handleCancel = () => {
    setRemoveConfirmation(false);
  };

  const handleShowConfirm = () => {
    setRemoveConfirmation(true);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={styles.title}>{isEdit ? 'Edit Link' : 'Generate Link'}</div>
      {isEdit &&
        <div className={styles.deleteWrapper}>
          <LocalConfirmation onConfirm={handleConfirmRemove} onCancel={handleCancel} open={removeConfirmation}>
            <Button disabled={editLoading || deleteLoading} type={'button'} onClick={handleShowConfirm}
                className={styles.removeBtn}
                color={'red'}
                size={'extraSmall'}
                label={'Delete link'}/>
          </LocalConfirmation>
        </div>
      }
      <form className={styles.form} onSubmit={isEdit ? handleEdit : handleSave}>
        <div className={styles.content}>
          <TextInput
            placeholder={'First name'}
            inline={true}
            label={'First Name'}
            id={'firstName'}
            value={firstName}
            onChange={handleChangeText(setFirstName)}
          />
          <TextInput
            placeholder={'Last name'}
            inline={true}
            label={'Last Name'}
            id={'lastName'}
            value={lastName}
            onChange={handleChangeText(setLastName)}
          />
          <TextInput
            allowSpecialCharacters
            placeholder={'Email'}
            inline={true}
            label={'Email'}
            id={'email'}
            value={email}
            onChange={handleChangeText(setEmail)}
          />
          <TextInput
            placeholder={'Association'}
            inline={true}
            label={'Association'}
            id={'association'}
            value={association}
            onChange={handleChangeText(setAssociation)}
          />
          <TextInput
            hasError={!!error.notes}
            subTitle={error.notes}
            placeholder={'Add notes'}
            inline={true}
            label={'Add Notes'}
            id={'notes'}
            value={notes}
            onChange={handleChangeText(setNotes, 'notes')}
            textArea={true}
          />
          <TextInput
            inline={true}
            label={'End Date'}
            id={'expireAt'}
            value={expireAt}
            onChange={handleChangeText(setExpireAt)}
            type={'date'}
            min={today}
          />
          {isEdit ? <>
            <div className={styles.row}>
              <div/>
              <Checkbox id={'blockAll'} checked={blockAll} onChange={handleChangeBlockAll} label={'Block all accounts invited with this link'}/>
            </div>
            <div className={styles.row}>
              <div/>
              <div>
                <Checkbox id={'expired'} checked={expired} onChange={handleChangeExpired} label={'Disable link'}/>
                <div className={styles.courseAction}>
                  <div className={`${styles.status} ${isActive ? styles.activeStatus : ''}`}>
                    {isActive ? 'Active' : 'Disabled'}
                  </div>
                </div>
              </div>
            </div>
          </> : null}
          {Boolean(beError) && <div className={styles.error}>{beError}</div>}
          <div className={styles.action}>
            <div/>
            {isEdit ?
              <Button disabled={editLoading || deleteLoading} type={'submit'} label={'Save'}/> :
              <Button disabled={createLoading} type={'submit'} label={'Generate Link'}/>
            }
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default AddModal;
